import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { create } from 'zustand';

import { useDrawerStore } from './drawer.store';

/**
 * PBQA drawer UI state — Contract Review Bot evaluation period.
 *
 * Holds cross-component state for the PBQA drawer:
 *   - `activeRunId`  which run the SME currently has selected (drives detail fetching)
 *   - `activeBlockId` which block the user has focused in the viewer; when set,
 *                     the drawer scrolls its findings list to that block.
 *
 * Kept alongside the other ContractBuilder stores so anything outside the
 * drawer (the header badge reads `activeRunId`) shares one source of truth
 * without prop-drilling. `activeBlockId` has no caller since the per-block
 * finding pill was removed; the drawer still honours it as a deep-link hook.
 */
interface PbqaEvalUIStore {
  activeBlockId: string | undefined;
  activeRunId: string | undefined;
  setActiveBlockId: (id: string | undefined) => void;
  setActiveRunId: (id: string | undefined) => void;
  reset: () => void;
}

export const usePbqaEvalUIStore = create<PbqaEvalUIStore>((set) => ({
  activeBlockId: undefined,
  activeRunId: undefined,
  setActiveBlockId: (id: string | undefined) => {
    set({ activeBlockId: id });

    // Opening from a block action: make sure the drawer is visible so the
    // finding for that block is actually on screen.
    if (id && !useDrawerStore.getState().isOpen) {
      useDrawerStore.getState().openDrawer({
        type: DrawerLayoutComponent.PbqaEval,
        props: {},
        width: 'wide',
      });
    }
  },
  setActiveRunId: (id: string | undefined) => set({ activeRunId: id }),
  reset: () => set({ activeBlockId: undefined, activeRunId: undefined }),
}));
